import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE } from '@/data/site-config';

export function Seo({ title, description, canonical, noindex = false, image, jsonLd = null, children }) {
  const fullTitle = title ? `${title} | ${SITE.name}` : `Personal Trainer in ${SITE.city}, ${SITE.stateAbbr} | ${SITE.name}`;
  const desc = description || `Private personal training, semi-private group training, nutrition coaching, golf fitness, and online coaching in ${SITE.city}, ${SITE.stateAbbr}. NASM-certified coach Jarad Wingard. Free consultation.`;
  const url = canonical ? `${SITE.publicUrl}${canonical}` : SITE.publicUrl;
  const ogImage = image || `${SITE.publicUrl}/og-image.png`;
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow, noarchive" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content="en_US" />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {image && <meta name="twitter:image" content={image} />}
      {/* Local SEO geo tags */}
      <meta name="geo.region" content={`US-${SITE.stateAbbr}`} />
      <meta name="geo.placename" content={`${SITE.city}, ${SITE.stateAbbr}`} />
      <meta name="geo.position" content={`${SITE.geo.lat};${SITE.geo.lng}`} />
      <meta name="ICBM" content={`${SITE.geo.lat}, ${SITE.geo.lng}`} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {children}
    </Helmet>
  );
}

const DAY_MAP = { Sunday: 'Su', Monday: 'Mo', Tuesday: 'Tu', Wednesday: 'We', Thursday: 'Th', Friday: 'Fr', Saturday: 'Sa' };

export const localBusinessJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': ['HealthClub', 'LocalBusiness'],
  '@id': `${SITE.publicUrl}#business`,
  name: SITE.name,
  alternateName: `${SITE.name} Private Studio`,
  description: 'NASM-certified private personal training studio in Stuart, Florida. Owned and coached by Jarad Wingard. Serving Martin County.',
  url: SITE.publicUrl,
  telephone: SITE.phone,
  email: SITE.email,
  image: [
    'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/seepc1jx_IMG_0224.jpeg',
    'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/b8emmccl_IMG_0223.jpeg',
    'https://customer-assets.emergentagent.com/job_e9fb7f82-473d-4f98-94a1-f75dc6277854/artifacts/jn9vslkw_IMG_0221.jpeg',
  ],
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE.address.line1,
    addressLocality: SITE.city,
    addressRegion: SITE.stateAbbr,
    postalCode: '34997',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: SITE.geo.lat,
    longitude: SITE.geo.lng,
  },
  openingHoursSpecification: SITE.hoursStructured.map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.day,
    opens: h.open,
    closes: h.close,
  })),
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Martin County, Florida' },
    { '@type': 'City', name: 'Stuart, FL' },
    { '@type': 'City', name: 'Palm City, FL' },
    { '@type': 'City', name: 'Jensen Beach, FL' },
    { '@type': 'City', name: 'Hobe Sound, FL' },
    { '@type': 'City', name: 'Port Salerno, FL' },
    { '@type': 'City', name: 'Sewall’s Point, FL' },
  ],
  founder: { '@type': 'Person', name: SITE.owner, jobTitle: 'NASM-Certified Personal Trainer' },
  employee: { '@type': 'Person', name: SITE.owner, jobTitle: 'Owner & NASM-Certified Personal Trainer' },
  sameAs: [SITE.social.facebook, SITE.social.instagram, SITE.booking.url].filter(Boolean),
  priceRange: '$$',
  paymentAccepted: 'Credit Card, Debit Card',
  slogan: 'Using 360 degrees of your body.',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Coaching services',
    itemListElement: [
      'Personal Training',
      'Semi-Private Personal Training',
      'Nutrition Coaching',
      'Golf Fitness Training',
      'Online Fitness Coaching',
    ].map((n) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: n } })),
  },
});

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: `${SITE.publicUrl}${it.path}`,
  })),
});

export const serviceJsonLd = (name, description, slug) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: name,
  provider: { '@type': 'HealthClub', name: SITE.name, url: SITE.publicUrl, telephone: SITE.phone },
  areaServed: { '@type': 'AdministrativeArea', name: 'Martin County, Florida' },
  url: `${SITE.publicUrl}/services/${slug}`,
  description,
  audience: { '@type': 'PeopleAudience', geographicArea: { '@type': 'AdministrativeArea', name: 'Martin County, Florida' } },
});

export const faqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});
